import type { IStorageModel } from "@models/storage/IStorageModel";
import type { IDocumentOpenService } from "@runtime/browser/BrowserDocumentOpenService";
import { isProductArchived, productName } from "@models/data/productDto";
import {
  docContentType,
  docFileNameFromStoragePath,
} from "@models/data/docsManifestDto";
import {
  normalizeProductNameKey,
  uniqueNormalizedNameKeys,
  productDocumentContentType,
  productDocumentFileSize,
  productDocumentFormat,
  productDocumentHasThumbnail,
  productDocumentLastModifiedMs,
  productDocumentDisplayName,
  productDocumentName,
  productDocumentNameKey,
  productDocumentProducts,
  productDocumentStoragePath,
  productDocumentUserTag,
  type ProductDocumentDto,
} from "@models/data/productDocumentDto";
import type { DocLibraryItem } from "./IProjectDocsModel";
import type {
  IProductDocsStoreModel,
  ProductDocsByKeysQuery,
  ProductReferenceStatus,
  ProductDocsQuery,
} from "./IProductDocsStoreModel";

/**
 * Read-only product document store backed by `projectstore-metadata`.
 *
 * Documents are discovered through sparse `documentByProduct` link rows, then
 * rendered from the canonical document rows. Management remains unavailable in
 * the browser until the Define app exposes a client-safe write contract.
 */
export class ProductDocsStoreModel implements IProductDocsStoreModel {
  readonly canWrite = false;

  constructor(
    private readonly m_storage: IStorageModel,
    private readonly m_documentOpen: IDocumentOpenService,
  ) {}

  async getDocsForProductAsync(
    query: ProductDocsQuery,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<DocLibraryItem>> {
    const productKey = normalizeProductNameKey(query.productKey);
    if (!query.customerId || !productKey) return [];
    const rows = await this.m_storage.listDocumentsByProductName(
      query.customerId,
      productKey,
      { includeArchived: false },
      signal,
    );

    return rows
      .map((row) => mapProductDocumentRowToItem(row, [productKey]))
      .filter((item): item is DocLibraryItem => item !== null);
  }

  async getDocsByKeysAsync(
    query: ProductDocsByKeysQuery,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<DocLibraryItem>> {
    const documentNameKeys = uniqueNormalizedNameKeys(query.documentNameKeys);
    if (!query.customerId || documentNameKeys.length === 0) return [];
    const rows = await this.m_storage.getDocumentsByNameKeys(
      query.customerId,
      documentNameKeys,
      { includeArchived: false },
      signal,
    );
    return rows
      .map((row) => mapProductDocumentRowToItem(row, []))
      .filter((item): item is DocLibraryItem => item !== null);
  }

  async getProductStatusAsync(
    query: ProductDocsQuery,
    signal?: AbortSignal,
  ): Promise<ProductReferenceStatus> {
    const productKey = normalizeProductNameKey(query.productKey);
    const displayFallback = productKey || query.productKey;
    if (!productKey) {
      return {
        productKey: "",
        state: "missing",
        displayName: displayFallback,
      };
    }
    try {
      const row = await this.m_storage.getProductByName(query.customerId, productKey, signal);
      if (row === null) {
        return {
          productKey,
          state: "missing",
          displayName: displayFallback,
        };
      }
      return {
        productKey,
        state: isProductArchived(row) ? "archived" : "found",
        displayName: productName(row) || displayFallback,
      };
    } catch (error) {
      return {
        productKey,
        state: "unavailable",
        displayName: displayFallback,
        reason: error instanceof Error ? error.message : String(error),
      };
    }
  }

  async getThumbnailUrlAsync(
    query: ProductDocsQuery,
    fileName: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    if (!query.customerId || !fileName) return null;
    return this.m_storage.getDocThumbnailBlobUrl(query.customerId, fileName, signal);
  }

  async openAsync(
    query: ProductDocsQuery,
    storagePath: string,
    signal?: AbortSignal,
  ): Promise<boolean> {
    if (!query.customerId || !storagePath) return false;
    const fileName = docFileNameFromStoragePath(storagePath) ?? storagePath;
    return this.m_documentOpen.openAsync(() =>
      this.m_storage.getDocBlobUrl(
        query.customerId,
        storagePath,
        productDocumentContentType({ storagePath }) ?? docContentType(fileName),
        signal,
      ),
    );
  }
}

function mapProductDocumentRowToItem(
  row: ProductDocumentDto,
  fallbackDependentProductNames: ReadonlyArray<string>,
): DocLibraryItem | null {
  const storagePath = productDocumentStoragePath(row);
  const fileName = productDocumentName(row) || docFileNameFromStoragePath(storagePath);
  if (!storagePath || !fileName) return null;
  const products = productDocumentProducts(row);
  const dependentProductNames = products.length > 0 ? products : [...fallbackDependentProductNames];
  return {
    storagePath,
    documentNameKey: productDocumentNameKey(row),
    fileName,
    displayName: productDocumentDisplayName(row),
    format: productDocumentFormat(row),
    fileSize: productDocumentFileSize(row),
    userTag: productDocumentUserTag(row),
    lastModifiedMs: productDocumentLastModifiedMs(row),
    hasThumbnail: productDocumentHasThumbnail(row),
    dependentProjectNames: dependentProductNames,
    usedInProjectCount: dependentProductNames.length,
    sources: [],
  };
}
